(function () {
  const TRACKING_CONSENT_COOKIE = '_tracking_consent';
  const SHOPIFY_POPUP_SELECTORS = [
    '#shopify-pc__banner__btn-accept',
    '#shopify-pc__banner__btn-decline',
    '#shopify-pc__prefs__header-accept',
    '#shopify-pc__prefs__header-decline',
    '#shopify-pc__prefs__header-save',
  ].join(',');
  const DENIED_CONSENT = {
    preferencesAllowed: false,
    analyticsAllowed: false,
    marketingAllowed: false,
  };

  let lastPushedConsentState = '';

  function getDataLayer() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  function gtag() {
    getDataLayer().push(arguments);
  }

  function toBoolean(value) {
    return value === true || value === 'true' || value === 'yes' || value === '1';
  }

  function normalizeConsent(detail) {
    const preferencesAllowed = toBoolean(detail?.preferencesAllowed ?? detail?.preferences ?? detail?.functionality);
    const analyticsAllowed = toBoolean(detail?.analyticsAllowed ?? detail?.analytics);
    const marketingAllowed = toBoolean(detail?.marketingAllowed ?? detail?.marketing);

    return {
      preferencesAllowed,
      analyticsAllowed,
      marketingAllowed,
    };
  }

  function getCookie(name) {
    const cookie = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(`${name}=`));

    return cookie ? decodeURIComponent(cookie.substring(name.length + 1)) : null;
  }

  function parseTrackingConsentCookie(value) {
    const match = value?.match(/^0([Ff])([Aa])([Mm])\.0$/);

    if (!match) return null;

    return {
      preferencesAllowed: match[1] === 'F',
      analyticsAllowed: match[2] === 'A',
      marketingAllowed: match[3] === 'M',
    };
  }

  function hasExplicitConsent(detail) {
    const consentKeys = [
      'preferencesAllowed',
      'analyticsAllowed',
      'marketingAllowed',
      'preferences',
      'analytics',
      'marketing',
    ];

    return consentKeys.some((key) => detail?.[key] !== undefined && detail?.[key] !== null && detail?.[key] !== '');
  }

  function consentModeState(consent) {
    return {
      ad_storage: consent.marketingAllowed ? 'granted' : 'denied',
      ad_user_data: consent.marketingAllowed ? 'granted' : 'denied',
      ad_personalization: consent.marketingAllowed ? 'granted' : 'denied',
      analytics_storage: consent.analyticsAllowed ? 'granted' : 'denied',
      functionality_storage: consent.preferencesAllowed ? 'granted' : 'denied',
      personalization_storage: consent.preferencesAllowed ? 'granted' : 'denied',
      security_storage: 'granted',
    };
  }

  function setTrackingConsentCookie(consent) {
    const flag = [
      '0',
      consent.preferencesAllowed ? 'F' : 'f',
      consent.analyticsAllowed ? 'A' : 'a',
      consent.marketingAllowed ? 'M' : 'm',
    ].join('');

    document.cookie = `${TRACKING_CONSENT_COOKIE}=${flag}.0; path=/; SameSite=Lax`;
  }

  function pushConsentUpdate(detail, source, options = {}) {
    const consent = detail?.preferencesAllowed !== undefined
      && detail?.analyticsAllowed !== undefined
      && detail?.marketingAllowed !== undefined
      ? detail
      : normalizeConsent(detail);
    const stateKey = JSON.stringify(consent);
    const shouldWriteCookie = options.writeCookie !== false;

    if (shouldWriteCookie) {
      setTrackingConsentCookie(consent);
    }

    if (stateKey === lastPushedConsentState) return;

    lastPushedConsentState = stateKey;

    gtag('consent', 'update', consentModeState(consent));
    getDataLayer().push({
      event: 'shopify_consent_update',
      consent_source: source,
      shopify_consent: consent,
    });
  }

  function getCustomerPrivacy() {
    return window.Shopify?.customerPrivacy || null;
  }

  function getCurrentShopifyConsent() {
    const customerPrivacy = getCustomerPrivacy();

    if (!customerPrivacy) return null;

    if (typeof customerPrivacy.currentVisitorConsent === 'function') {
      return customerPrivacy.currentVisitorConsent();
    }

    return null;
  }

  function shouldShowShopifyBanner() {
    const customerPrivacy = getCustomerPrivacy();

    if (!customerPrivacy || typeof customerPrivacy.shouldShowBanner !== 'function') return null;

    try {
      return customerPrivacy.shouldShowBanner();
    } catch (error) {
      return null;
    }
  }

  function syncCurrentConsent(options = {}) {
    const cookieConsent = parseTrackingConsentCookie(getCookie(TRACKING_CONSENT_COOKIE));

    if (cookieConsent) {
      pushConsentUpdate(cookieConsent, 'tracking_consent_cookie', { writeCookie: false });
      return true;
    }

    const currentConsent = getCurrentShopifyConsent();

    if (currentConsent && hasExplicitConsent(currentConsent)) {
      pushConsentUpdate(currentConsent, 'shopify_current_visitor_consent', options);
      return true;
    }

    if (shouldShowShopifyBanner() === false) {
      pushConsentUpdate(DENIED_CONSENT, 'shopify_no_banner_default_denied', { writeCookie: true });
      return true;
    }

    return shouldShowShopifyBanner() === true;
  }

  function syncCurrentConsentWithRetry(options = {}, attempts = 10) {
    const handled = syncCurrentConsent(options);

    if (handled || attempts <= 0) return;

    setTimeout(() => syncCurrentConsentWithRetry(options, attempts - 1), 500);
  }

  document.addEventListener('visitorConsentCollected', (event) => {
    pushConsentUpdate(event.detail, 'visitor_consent_collected');
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(SHOPIFY_POPUP_SELECTORS)) return;

    setTimeout(() => syncCurrentConsentWithRetry({ writeCookie: true }, 3), 250);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncCurrentConsentWithRetry({ writeCookie: true }), { once: true });
  } else {
    syncCurrentConsentWithRetry({ writeCookie: true });
  }
})();
